import type { Unsubscribe } from './disposables.ts'
import { createLogger } from './logger.ts'

export type StoreListener<T> = (state: T, prev: T) => void

/** The read side of a store, which is all the UI gets. */
export interface ReadableStore<T> {
  get(): T
  subscribe(listener: StoreListener<T>): Unsubscribe
}

export interface Store<T> extends ReadableStore<T> {
  /** Replaces the state. Listeners aren't notified when the result is `Object.is`-equal. */
  set(next: T | ((prev: T) => T)): void
}

const log = createLogger('store')

/** State is treated as immutable: always pass a new object, never mutate the current one. */
export function createStore<T>(initial: T): Store<T> {
  let state = initial
  const listeners = new Set<StoreListener<T>>()

  return {
    get: () => state,
    set(next) {
      const prev = state
      state = typeof next === 'function' ? (next as (prev: T) => T)(prev) : next
      if (Object.is(state, prev)) return
      for (const listener of [...listeners]) {
        try {
          listener(state, prev)
        } catch (error) {
          log.error('Listener failed', error)
        }
      }
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
